import {
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_PRIVATE_TRANSFER_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
} from 'constants/experience'

export const EXP_CATEGORY_TRANSFERS_SEARCH_ID = -1001
export const EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID = -1002

// Temporary search UI-only A/B mapping. These are not real experience categories
// and must not be reused outside this search-page experiment. Remove this layer
// after the A/B is validated, either by applying the taxonomy change properly or
// reverting the experiment.
export const TRAVEL_ESSENTIALS_TRANSFER_CATEGORY_IDS = [
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_PRIVATE_TRANSFER_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
]

export const EXP_CATEGORY_TRANSFERS_AND_LOUNGES_REAL_CATEGORY_IDS = [
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  ...TRAVEL_ESSENTIALS_TRANSFER_CATEGORY_IDS,
]

export const TRAVEL_ESSENTIALS_PARENT_CATEGORY_IDS = [EXP_CATEGORY_DAY_TRIPS_ID]

const TRAVEL_ESSENTIALS_TRANSFER_CATEGORY_ID_SET = new Set(TRAVEL_ESSENTIALS_TRANSFER_CATEGORY_IDS)

export interface TravelEssentialsSplitLabels {
  transfers: string
  loungesAndServices: string
}

interface CategoryGroup {
  parent: App.ExperienceItemCategory
  children: Array<App.ExperienceItemCategory>
  count: number
  relevanceScore: number
}

interface FilterItem {
  value: number
  count: number
  checked: boolean
}

interface FilterCategory<TItem extends FilterItem> {
  category: App.ExperienceItemCategory
  categoryId: number
  count: number
  items: Array<TItem>
  name: string
  title: string
}

export function isTravelEssentialsCategory(category: App.ExperienceItemCategory) {
  return category.id === EXP_CATEGORY_DAY_TRIPS_ID
}

export function isTravelEssentialsChildCategory(category: App.ExperienceItemCategory) {
  return category.parentId === EXP_CATEGORY_DAY_TRIPS_ID
}

export function isTravelEssentialsSearchCategoryId(categoryId: number) {
  return categoryId === EXP_CATEGORY_TRANSFERS_SEARCH_ID ||
    categoryId === EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID
}

export function isTravelEssentialsTransferCategoryId(categoryId: number) {
  return TRAVEL_ESSENTIALS_TRANSFER_CATEGORY_ID_SET.has(categoryId)
}

export function getTravelEssentialsRealCategoryIds(categories: Array<App.ExperienceItemCategory>) {
  return [
    EXP_CATEGORY_DAY_TRIPS_ID,
    ...categories
      .filter(isTravelEssentialsChildCategory)
      .map((category) => category.id),
  ]
}

export function getNextCategoryCodesForSearchCategory(
  currentCategoryCodes: Array<number>,
  searchCategoryId: number,
  itemCategoryIds: Array<number>,
  selectedCategoryIds: Array<number>,
) {
  // Virtual A/B categories must always map back to real category IDs before
  // reaching search filters/API calls. Do not persist or treat them as domain data.
  const relatedCategoryIds = new Set([
    searchCategoryId,
    ...itemCategoryIds,
  ])

  if (isTravelEssentialsSearchCategoryId(searchCategoryId)) {
    TRAVEL_ESSENTIALS_PARENT_CATEGORY_IDS.forEach((categoryId) => relatedCategoryIds.add(categoryId))
  }

  return [
    ...currentCategoryCodes.filter((categoryId) => !relatedCategoryIds.has(categoryId)),
    ...selectedCategoryIds,
  ]
}

export function splitTravelEssentialsCategoryGroups(
  categoryGroups: Array<CategoryGroup>,
  labels: TravelEssentialsSplitLabels,
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>,
): Array<CategoryGroup> {
  return categoryGroups.flatMap((categoryGroup) => {
    if (!isTravelEssentialsCategory(categoryGroup.parent)) {
      return [categoryGroup]
    }

    const transferChildren = categoryGroup.children.filter((category) =>
      isTravelEssentialsTransferCategoryId(category.id),
    )
    const loungesAndServicesChildren = categoryGroup.children.filter((category) =>
      !isTravelEssentialsTransferCategoryId(category.id),
    )

    return [
      createCategoryGroup(categoryGroup, EXP_CATEGORY_TRANSFERS_SEARCH_ID, labels.transfers, transferChildren, categoryFacetsById),
      createCategoryGroup(categoryGroup, EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID, labels.loungesAndServices, loungesAndServicesChildren, categoryFacetsById),
    ].filter((group) => group.children.length > 0)
  })
}

export function splitTravelEssentialsFilterCategories<
  TItem extends FilterItem,
  TCategory extends FilterCategory<TItem>,
>(
  categories: Array<TCategory>,
  labels: TravelEssentialsSplitLabels,
): Array<TCategory> {
  return categories.flatMap((categoryItem) => {
    if (!isTravelEssentialsCategory(categoryItem.category)) {
      return [categoryItem]
    }

    const transferItems = categoryItem.items.filter((item) =>
      isTravelEssentialsTransferCategoryId(item.value),
    )
    const loungesAndServicesItems = categoryItem.items.filter((item) =>
      !isTravelEssentialsTransferCategoryId(item.value),
    )

    return [
      createFilterCategory(categoryItem, EXP_CATEGORY_TRANSFERS_SEARCH_ID, labels.transfers, transferItems),
      createFilterCategory(categoryItem, EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID, labels.loungesAndServices, loungesAndServicesItems),
    ].filter((item) => item.items.length > 0)
  })
}

function createCategoryGroup(
  categoryGroup: CategoryGroup,
  categoryId: number,
  name: string,
  children: Array<App.ExperienceItemCategory>,
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>,
): CategoryGroup {
  return {
    parent: createVirtualParentCategory(categoryGroup.parent, categoryId, name),
    children,
    count: sumChildCounts(children, categoryFacetsById),
    relevanceScore: Math.max(
      0,
      ...children.map((child) => categoryFacetsById[child.id]?.relevanceScore ?? 0),
    ),
  }
}

function createFilterCategory<
  TItem extends FilterItem,
  TCategory extends FilterCategory<TItem>,
>(
  categoryItem: TCategory,
  categoryId: number,
  name: string,
  items: Array<TItem>,
): TCategory {
  return {
    ...categoryItem,
    category: createVirtualParentCategory(categoryItem.category, categoryId, name),
    categoryId,
    count: items.reduce((total, item) => total + item.count, 0),
    items,
    name,
    title: name,
  } as TCategory
}

function createVirtualParentCategory(
  sourceCategory: App.ExperienceItemCategory,
  categoryId: number,
  name: string,
): App.ExperienceItemCategory {
  return {
    ...sourceCategory,
    id: categoryId,
    name,
  }
}

function sumChildCounts(
  children: Array<App.ExperienceItemCategory>,
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>,
) {
  return children.reduce((total, child) => {
    return total + (categoryFacetsById[child.id]?.count ?? 0)
  }, 0)
}
